/**
 * AR Exception Report - Pivot Table Component
 *
 * Client-level aggregated view with drill-down capability
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { Select, type SelectOption, Icon, showToast } from '@repo/ui';
import { DataTable, type DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import { ExportButton, type ExportFormat } from '../../../components';
import { useArAvailableClients, useArPivot, useArExport } from '../../../queries/arReportQuery';
import type { ArFiltersState } from '../../../queries/arReportQuery';
import { numberUtil } from '@xtnd/web/shared/utils';
import { REPORTS_ROUTES } from '@xtnd/web/shared/constants';
import { useReportFilters } from '../../../hooks';

const FLAG_COLORS: Record<any, { bg: string; text: string; border: string }> = {
  OK: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  VARIANCE_NON_ZERO: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  MISSING_IN_QBO: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  MISSING_IN_CLIO: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  DUPLICATE: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
};

const FLAG_LABELS: Record<any, string> = {
  OK: 'OK',
  VARIANCE_NON_ZERO: 'Variance',
  MISSING_IN_QBO: 'Missing QBO',
  MISSING_IN_CLIO: 'Missing Clio',
  DUPLICATE: 'Duplicate',
};

export function ArPivotTable() {
  const navigate = useNavigate();
  const { filters, pagination, updateFilters, updatePagination } =
    useReportFilters<ArFiltersState>();

  // Fetch data
  const { data, isLoading, error, refetch } = useArPivot(
    { ...filters, page: pagination.page, limit: pagination.limit },
    { enabled: true }
  );

  // Export mutation
  const exportMutation = useArExport();

  const handleExport = async (format: ExportFormat) => {
    try {
      const exportType = format === 'excel' ? 'xls' : format;
      await exportMutation.mutateAsync({
        type: exportType as 'csv' | 'xls' | 'pdf',
        scope: 'pivot',
        filters: filters || {},
      });
      showToast.success(`Export as ${format.toUpperCase()} started successfully`);
    } catch (error) {
      showToast.error(
        `Failed to export: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Get available clients for filters
  const { data: clientsData, isLoading: clientsLoading } = useArAvailableClients();

  const availableClients = useMemo(() => {
    if (!clientsData) return [];
    return clientsData.map((client: any) => ({
      id: client.clientId || client.id,
      name: client.clientName || client.name,
    }));
  }, [clientsData]);

  // Convert available clients to options format
  const clientOptions: SelectOption[] = useMemo(() => {
    return availableClients.map(client => ({
      value: client.id,
      label: client.name,
    }));
  }, [availableClients]);

  // Get selected client options
  const selectedClientOptions: SelectOption[] = useMemo(() => {
    if (!filters || !filters?.clients) return [];
    return availableClients
      .filter(client => filters.clients.includes(client.id))
      .map(client => ({
        value: client.id,
        label: client.name,
      }));
  }, [availableClients, filters]);

  // Handle client selection change
  const handleClientChange = (newValue: MultiValue<SelectOption> | null) => {
    const clients = newValue?.map(option => option.value) || [];
    const newFilters: ArFiltersState = {
      ...filters,
      clients,
    };
    updateFilters(newFilters, true); // Reset page to 1 when filters change
  };

  // Define columns for DataTable
  const columns: DataTableColumn<any>[] = useMemo(() => {
    // Handle client click to navigate to all invoices view with filter
    const handleClientClick = (clientId: string) => {
      const params = new URLSearchParams();
      params.set('tab', 'all-invoices');
      params.append('clients', clientId);
      navigate(`${REPORTS_ROUTES.AR.DASHBOARD}?${params.toString()}`);
    };

    return [
      {
        key: 'client',
        header: 'Client',
        align: 'left',
        renderCell: (client: any) => (
          <div
            className="flex items-center cursor-pointer hover:opacity-80 transition-opacity"
            role="button"
            tabIndex={0}
            onClick={() => handleClientClick(client.clientId)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClientClick(client.clientId);
              }
            }}
          >
            <div>
              <div className="space-y-1">
                {client.clioClientName && client.clioClientName !== 'N/A' && (
                  <div className="text-xs text-blue-600 dark:text-blue-400">
                    Clio: {client.clioClientName}
                  </div>
                )}
                {client.qboClientName && client.qboClientName !== 'N/A' && (
                  <div className="text-xs text-green-600 dark:text-green-400">
                    QBO: {client.qboClientName}
                  </div>
                )}
                {(!client.clioClientName || client.clioClientName === 'N/A') &&
                  (!client.qboClientName || client.qboClientName === 'N/A') && (
                    <div className="text-sm font-medium text-xtnd-dark dark:text-xtnd-light">
                      ID: {client.clientId}
                    </div>
                  )}
              </div>
              {client.invoicesWithVariance > 0 && (
                <div className="text-xs text-red-500 mt-1">
                  {client.invoicesWithVariance} issue
                  {client.invoicesWithVariance !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: 'clioOpen',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiDatabase" className="h-3 w-3" />
            Clio Open
          </div>
        ),
        align: 'right',
        headerClassName: 'bg-blue-50 dark:bg-blue-900/30',
        className: 'text-blue-700 dark:text-blue-200',
        renderCell: (client: any) => (
          <span className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
            {numberUtil.formatCurrency(client.sumOpenClio)}
          </span>
        ),
      },
      {
        key: 'qboOpen',
        header: (
          <div className="flex items-center justify-end gap-1">
            <Icon name="HiCash" className="h-3 w-3" />
            QBO Open
          </div>
        ),
        align: 'right',
        headerClassName: 'bg-green-50 dark:bg-green-900/30',
        className: 'text-green-700 dark:text-green-200',
        renderCell: (client: any) => (
          <span className="text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50">
            {numberUtil.formatCurrency(client.sumOpenQbo)}
          </span>
        ),
      },
      {
        key: 'invoices',
        header: 'Invoices',
        align: 'right',
        renderCell: (client: any) => (
          <span className="text-sm text-xtnd-dark dark:text-xtnd-light">{client.invoiceCount}</span>
        ),
      },
      {
        key: 'withVariance',
        header: 'With Variance',
        align: 'right',
        renderCell: (client: any) =>
          client.invoicesWithVariance > 0 ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {client.invoicesWithVariance}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              0
            </span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        align: 'left',
        renderCell: (client: any) => (
          <div className="flex flex-wrap gap-1">
            {client.flags.slice(0, 2).map((flag: any, flagIndex: number) => {
              const flagColor = FLAG_COLORS[flag] || {
                bg: 'bg-xtnd-dark-50',
                text: 'text-xtnd-dark-600',
                border: 'border-xtnd-dark-100',
              };
              const flagLabel = FLAG_LABELS[flag] || flag;
              return (
                <span
                  key={flagIndex}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${flagColor.bg} ${flagColor.text} ${flagColor.border} border`}
                  title={flagLabel}
                >
                  {flagLabel}
                </span>
              );
            })}
            {client.flags.length > 2 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-xtnd-dark-50 text-xtnd-dark-500 border border-xtnd-dark-100">
                +{client.flags.length - 2}
              </span>
            )}
          </div>
        ),
      },
    ];
  }, [navigate]);

  const renderHeader = () => (
    <>
      {/* Filters Section */}
      <div className="border-b border-xtnd-light dark:border-xtnd-dark-700 pb-4 mb-6">
        <div className="flex flex-wrap items-start gap-2 md:items-center md:flex-nowrap">
          {/* Client Filter */}
          <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
            <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
              Client:
            </span>
            <div className="flex-1">
              <Select<SelectOption>
                value={selectedClientOptions}
                onChange={handleClientChange}
                options={clientOptions}
                placeholder="Filter by client..."
                disabled={clientsLoading || isLoading}
                isLoading={clientsLoading || isLoading}
                multiple={true}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                clearable={true}
                searchable={true}
                menuPlacement="auto"
                menuPosition="fixed"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
          </div>

          {/* Selected counters */}
          <div className="w-full md:basis-[10%] md:w-auto">
            <div className="flex flex-col items-start md:items-end gap-1 mt-2 md:mt-0">
              {filters?.clients && filters.clients.length > 0 && (
                <span className="text-xs text-xtnd-dark-500 dark:text-xtnd-light whitespace-nowrap">
                  {filters.clients.length} client{filters.clients.length > 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      error={error}
      onRetry={() => refetch()}
      pagination={pagination}
      onPaginationChange={updatePagination}
      renderHeader={renderHeader}
      renderAction={() => (
        <ExportButton
          onExport={handleExport}
          disabled={isLoading}
          loading={exportMutation.isPending}
        />
      )}
      emptyState={{
        icon: 'HiUsers',
        title: 'No client data available',
        description: 'There are no clients matching your current filters.',
      }}
      tableContainerClassName="overflow-y-auto relative"
      tableClassName="min-w-full divide-y divide-xtnd-dark-100 relative"
      stickyHeader={true}
      tableMaxHeight="calc(100vh-300px)"
      pageSizeOptions={[25, 50, 100, 200]}
      loadingRowCount={5}
      loadingColumnCount={7}
    />
  );
}
